import { Paper, Stack, Typography } from "@mui/material";
import { Link } from "react-router";

import { GqlFilterOption } from "~/__generated__";
import useCreateClientUrl from "~/modules/client/hooks/useCreateClientUrl";
import { MapEditorFunctionTypeTreeFragment } from "~/modules/mapEditor/components/MapEditorEcologicalFunctionTypeTree/fragments.generated";
import { MapEditorFilters } from "~/modules/mapEditor/components/MapEditorFilters";
import { MapEditorObservationTypeTreeFragment } from "~/modules/mapEditor/components/MapEditorObservationTypeTree/fragments.generated";
import { MapEditorProtocolTreeFragment } from "~/modules/mapEditor/components/MapEditorProtocolTree/fragments.generated";
import { MapEditorResearchTypeTreeFragment } from "~/modules/mapEditor/components/MapEditorResearchTypeTree/fragments.generated";
import { MapPillPanelLayers } from "~/modules/mapEditor/components/MapPillPanelLayers";
import { MapPillPanelLayersFragment } from "~/modules/mapEditor/components/MapPillPanelLayers/fragments.generated";
import { GqlProjectMetadata } from "~/modules/project/api/useMeGetProjectInfoQuery";
import useCreateProjectUrl from "~/modules/project/hooks/useCreateProjectUrl";
import { ProjectMapEditorFilterVars } from "~/modules/project/hooks/useMapFilters";

type ProjectMapEditorLeftPanelProps = {
    project: GqlProjectMetadata;
    filterOptions: GqlFilterOption[];
    setFilter: <K extends keyof ProjectMapEditorFilterVars>(key: K, value: ProjectMapEditorFilterVars[K] | undefined) => void;
    filters: ProjectMapEditorFilterVars;
    onSelect: (layerId?: string) => void;
    selectedLayerId?: string;
    functionTypeTreeData?: MapEditorFunctionTypeTreeFragment[];
    observationTypeTreeData?: MapEditorObservationTypeTreeFragment[];
    protocolTreeData?: MapEditorProtocolTreeFragment | null;
    researchTypeData?: MapEditorResearchTypeTreeFragment[];
    layersData?: MapPillPanelLayersFragment[];
};

export function ProjectMapEditorLeftPanel({
    project,
    filterOptions,
    setFilter,
    filters,
    onSelect,
    selectedLayerId,
    functionTypeTreeData,
    observationTypeTreeData,
    protocolTreeData,
    researchTypeData,
    layersData,
}: ProjectMapEditorLeftPanelProps) {
    const createProjectUrl = useCreateProjectUrl();
    const createClientUrl = useCreateClientUrl();

    return (
        <>
            <Paper component={Stack} padding={2}>
                <Typography
                    variant="overline"
                    component={Link}
                    to={createClientUrl()}
                    color="inherit"
                    sx={{
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" },
                    }}
                >
                    {project.client.name}
                </Typography>
                <Typography
                    variant="h6"
                    component={Link}
                    to={createProjectUrl()}
                    color="inherit"
                    sx={{
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" },
                    }}
                >
                    {project.name}
                </Typography>
            </Paper>

            <MapPillPanelLayers
                onSelect={onSelect}
                selectedLayerId={selectedLayerId}
                layers={layersData}
                projectId={project.id}
            />

            <MapEditorFilters
                filterOptions={filterOptions}
                filters={filters}
                setFilter={setFilter}
                functionTypeTreeData={functionTypeTreeData}
                observationTypeTreeData={observationTypeTreeData}
                researchTypeData={researchTypeData}
                protocolTreeData={protocolTreeData}
            />
        </>
    );
}
